import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  Badge,
  Box,
  CollapsableCard,
  Divider,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon, F0Select } from '@factorialco/f0-react'
import { ChevronDown, ChevronRight, Delete, Plus, TextSize } from '@factorialco/f0-react/icons/app'

import { WorkflowManifestFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { getFieldErrors } from 'modules/workflows/components/Node/utils'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { ConditionalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/conditionalFormSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import { WorkflowsComparison, WorkflowsExpression } from 'modules/workflows/lib/types'

import { RightHandSelector, VariableData } from './RightHandSelector'
import {
  DEFAULT_COMPARISON,
  EMPTY_ARRAY_SELECTOR_VALUE,
  EMPTY_PEOPLE_GROUP_SPECS_VALUE,
  EMPTY_SELECTOR_VALUE,
} from './constants'
import { filterStaticInputVariables, getSelectedVariableNames } from './variableFiltering'
import { getComparisonErrorMessages } from './errors'
import { getLiteralType } from './literalTypes'
import { getOperatorValuesForVariable } from './operators'
import { LiteralTypeEnum, OperatorEnum, VariableTypeEnum } from './types'

type WorkflowDataInput = WorkflowManifestFragment['workflowDataInputs'][number]

// Returns the base variable type used by getLiteralType.
// multiSelect returns 'string' here because the operator-based logic in
// getLiteralType handles contains/not_contains before the type switch.
const extractVariableType = (input?: WorkflowDataInput): VariableTypeEnum | undefined => {
  if (input?.multiSelectType) return 'string'
  return input?.simpleType?.kind ?? (input?.enumType ? 'string' : undefined)
}

const isPeopleGroupOperator = (operator?: OperatorEnum): boolean =>
  operator === 'in_people_group' || operator === 'not_in_people_group'

const ConditionalPathCardContent = ({
  comparisonPos,
  expression,
  setExpression,
  variablesConfig,
  conditionsHeaderLabel = i18n.t('workflows.sidepanel.condition.paths.conditions_header'),
  dynamicInputsHeaderLabel = i18n.t('workflows.sidepanel.condition.paths.dynamic_inputs_header'),
}: {
  comparisonPos: number
  expression: WorkflowsExpression
  setExpression: (newExpression: WorkflowsExpression) => void
  variablesConfig: VariablesConfig
  conditionsHeaderLabel?: string
  dynamicInputsHeaderLabel?: string
}) => {
  const { workflowManifest } = useWorkflowContext()
  const { formState, getFieldState, clearErrors } = useFormContext<ConditionalFormSchema>()

  const operatorLabel = useCallback((value: OperatorEnum): string => {
    switch (value) {
      case 'eq':
        return i18n.t('workflows.node_type.condition.operations.eq')
      case 'neq':
        return i18n.t('workflows.node_type.condition.operations.neq')
      case 'one_of':
        return i18n.t('workflows.node_type.condition.operations.one_of')
      case 'not_one_of':
        return i18n.t('workflows.node_type.condition.operations.not_one_of')
      case 'contains':
        return i18n.t('workflows.node_type.condition.operations.contains')
      case 'not_contains':
        return i18n.t('workflows.node_type.condition.operations.not_contains')
      case 'gt':
        return i18n.t('workflows.node_type.condition.operations.gt')
      case 'gte':
        return i18n.t('workflows.node_type.condition.operations.gte')
      case 'lt':
        return i18n.t('workflows.node_type.condition.operations.lt')
      case 'lte':
        return i18n.t('workflows.node_type.condition.operations.lte')
      case 'in_people_group':
        return i18n.t('workflows.node_type.condition.operations.in_people_group')
      case 'not_in_people_group':
        return i18n.t('workflows.node_type.condition.operations.not_in_people_group')
      default:
        return String(value)
    }
  }, [])

  const operatorOptionsFor = useCallback(
    (variableName?: string) =>
      getOperatorValuesForVariable(variablesConfig, variableName).map((value) => ({
        value,
        label: operatorLabel(value),
      })),
    [operatorLabel, variablesConfig]
  )

  const findInputByName = useCallback(
    (name: string) =>
      workflowManifest.workflowDataInputs.find((i) => i.name === name) ??
      workflowManifest.dynamicWorkflowDataInputs.find((i) => i.name === name),
    [workflowManifest.workflowDataInputs, workflowManifest.dynamicWorkflowDataInputs]
  )

  const getVariableData = useCallback(
    (name?: string, source?: 'input' | 'dynamic_input'): VariableData | undefined => {
      if (!name) return undefined

      const findIn = (src: 'input' | 'dynamic_input') =>
        src === 'input'
          ? workflowManifest.workflowDataInputs.find((i) => i.name === name)
          : workflowManifest.dynamicWorkflowDataInputs.find((i) => i.name === name)

      const input = source
        ? (findIn(source) ?? findIn(source === 'input' ? 'dynamic_input' : 'input'))
        : (findIn('input') ?? findIn('dynamic_input'))

      if (!input) return undefined

      return {
        name: input.name,
        type: extractVariableType(input) ?? 'string',
        options: input.enumType?.options ?? input.multiSelectType?.options ?? null,
        unit: null,
      }
    },
    [workflowManifest.workflowDataInputs, workflowManifest.dynamicWorkflowDataInputs]
  )

  const selectedVariableNames = useMemo(
    () => getSelectedVariableNames(expression.root.expressions),
    [expression.root.expressions]
  )

  const staticInputVariables = useMemo(
    () =>
      filterStaticInputVariables(
        workflowManifest.workflowDataInputs,
        variablesConfig,
        selectedVariableNames
      ),
    [workflowManifest.workflowDataInputs, variablesConfig, selectedVariableNames]
  )

  const dynamicInputVariables = useMemo(
    () =>
      workflowManifest.dynamicWorkflowDataInputs.map((input) => {
        const configLabel = variablesConfig && variablesConfig[input.name]?.label
        return {
          label: configLabel ?? input.name,
          value: input.name,
          source: 'dynamic_input' as const,
        }
      }),
    [workflowManifest.dynamicWorkflowDataInputs, variablesConfig]
  )

  // Grouped options for the select dropdown (matching the design)
  const selectOptions = useMemo(() => {
    const options: Array<
      | { label: string; value: string; disabled?: boolean; icon?: typeof TextSize }
      | { type: 'separator' }
    > = []

    // Add "Conditions" group (static workflow inputs)
    if (staticInputVariables.length > 0) {
      options.push({
        value: 'header_conditions',
        label: conditionsHeaderLabel,
        disabled: true,
      })
      staticInputVariables
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach((variable) => {
          options.push({
            label: variable.label,
            value: variable.value,
            icon: variable.icon,
            ...(variable.hidden ? { disabled: true } : {}),
          })
        })
    }

    // Add "Form questions" group (dynamic inputs)
    if (dynamicInputVariables.length > 0) {
      if (staticInputVariables.length > 0) {
        options.push({ type: 'separator' })
      }
      options.push({
        value: 'header_dynamic_inputs',
        label: dynamicInputsHeaderLabel,
        disabled: true,
      })
      dynamicInputVariables
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach((variable) => {
          options.push({ label: variable.label, value: variable.value, icon: TextSize })
        })
    }

    return options
  }, [staticInputVariables, dynamicInputVariables, conditionsHeaderLabel, dynamicInputsHeaderLabel])

  const comparisons = expression.root.expressions
  const firstComparison = comparisons[0]

  const handleDeleteComparison = (comparisonIndex: number) => {
    const newExpressions = comparisons.filter((_, index) => index !== comparisonIndex)

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleLiteralSelection = (
    comparisonIndex: number,
    newValue: string,
    type: LiteralTypeEnum
  ) => {
    // Validate the value based on type
    // For number types, allow empty string (for in-progress editing) or valid numbers
    if (type === 'integer' || type === 'float') {
      // Allow empty string or valid number format (including negative and decimal)
      if (newValue !== '' && !/^-?\d*\.?\d*$/.test(newValue)) {
        // If invalid format, don't update
        return
      }
    }

    const newExpressions = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        return {
          ...comparison,
          right: { value: newValue, type },
        }
      }
      return comparison
    })

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const createLiteralChangeHandler =
    (comparisonIndex: number, comparison: WorkflowsComparison) => (newValue: string) => {
      const name = comparison.left.name
      if (!name) return

      const variableData = findInputByName(name)
      const type = getLiteralType(
        extractVariableType(variableData),
        comparison.operator,
        !!variableData?.enumType
      )
      handleLiteralSelection(comparisonIndex, newValue, type)
    }

  const handleVariableSelection = (comparisonIndex: number, variableName: string) => {
    const staticInput = workflowManifest.workflowDataInputs.find(
      (data) => data.name === variableName
    )

    const variableData = staticInput ?? findInputByName(variableName)

    if (!variableData) {
      throw new Error(`Variable data not found for: ${variableName}`)
    }

    const source: 'input' | 'dynamic_input' = staticInput ? 'input' : 'dynamic_input'

    // Check if variable is a people_group_target type
    const varConfig = variablesConfig[variableName]
    const isPeopleGroupTarget = varConfig?.type === 'people_group_target'

    // Whenever variable changes, reset operator to the first valid operator for the variable type.
    // multiSelect variables only support contains/not_contains, so defaulting to 'eq' would be invalid.
    const isMultiSelectVariable = !!variableData.multiSelectType
    const nextOperator: OperatorEnum = isPeopleGroupTarget
      ? 'in_people_group'
      : isMultiSelectVariable
        ? 'contains'
        : 'eq'
    const type = getLiteralType(
      extractVariableType(variableData),
      nextOperator,
      !!variableData.enumType
    )
    const nextValue = isPeopleGroupTarget ? EMPTY_PEOPLE_GROUP_SPECS_VALUE : EMPTY_SELECTOR_VALUE

    const newExpressions: WorkflowsComparison[] = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        return {
          ...comparison,
          left: { name: variableName, source: source },
          operator: nextOperator,
          right: { value: nextValue, type: type },
        }
      }
      return comparison
    })

    // Clear validation errors for this expression when variable changes
    // This prevents stale errors from a previous variable selection from persisting
    clearErrors(`comparisons.${comparisonPos}.expression.root.expressions.${comparisonIndex}`)

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleOperatorSelection = (comparisonIndex: number, operator: OperatorEnum) => {
    const newExpressions = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        const previousOperator = comparison.operator
        // Operators that use array values on the right-hand side (scalar IN set)
        const isRightArrayOperator = (op: string) => op === 'one_of' || op === 'not_one_of'
        // Operators that use array values on the left-hand side (set CONTAINS scalar)
        const isLeftArrayOperator = (op: string) => op === 'contains' || op === 'not_contains'

        const wasPeopleGroup = isPeopleGroupOperator(previousOperator)
        const isPeopleGroup = isPeopleGroupOperator(operator)

        // When switching to/from people group operators, reset value
        if (wasPeopleGroup !== isPeopleGroup) {
          const variableData = comparison.left.name
            ? findInputByName(comparison.left.name)
            : undefined

          const newType = getLiteralType(
            extractVariableType(variableData),
            operator,
            !!variableData?.enumType
          )
          const newValue = isPeopleGroup ? EMPTY_PEOPLE_GROUP_SPECS_VALUE : EMPTY_SELECTOR_VALUE

          return {
            ...comparison,
            operator: operator,
            right: { value: newValue, type: newType },
          }
        }

        const operatorCategoryChanged =
          isRightArrayOperator(previousOperator) !== isRightArrayOperator(operator) ||
          isLeftArrayOperator(previousOperator) !== isLeftArrayOperator(operator)

        // Reset value when switching between operator categories that use different value formats
        if (operatorCategoryChanged) {
          const variableData = comparison.left.name
            ? findInputByName(comparison.left.name)
            : undefined

          const newType = getLiteralType(
            extractVariableType(variableData),
            operator,
            !!variableData?.enumType
          )
          const newValue = isRightArrayOperator(operator)
            ? EMPTY_ARRAY_SELECTOR_VALUE
            : EMPTY_SELECTOR_VALUE

          return {
            ...comparison,
            operator: operator,
            right: { value: newValue, type: newType },
          }
        }

        return {
          ...comparison,
          operator: operator,
        }
      }
      return comparison
    })

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleAddRequirement = () => {
    const newExpressions = [...comparisons, DEFAULT_COMPARISON]

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const errors = getFieldState(
    `comparisons.${comparisonPos}.expression.root.expressions.0`,
    formState
  ).error
  const { leftMessage, operatorMessage, rightMessage } = getComparisonErrorMessages(errors)
  const { variable, operator, value } = getFieldErrors(expression, 0)

  return (
    <Box
      border={{ top: { width: 's1', color: 'grey300', style: 'solid' } }}
      padding="s16"
      gap="s16"
    >
      <Box flexDirection="row" gap="s8" alignItems="flexStart" width="full">
        <Box style={{ flex: '1', minWidth: '0' }}>
          <F0Select
            size="md"
            hideLabel
            clearable={false}
            label={i18n.t('workflows.sidepanel.condition.paths.type')}
            placeholder={i18n.t('workflows.sidepanel.condition.paths.select_placeholder')}
            options={selectOptions}
            value={firstComparison?.left.name}
            onChange={(variableName: string) => handleVariableSelection(0, variableName)}
            error={variable ? leftMessage : undefined}
          />
        </Box>

        <Box style={{ flex: '1', minWidth: '0' }}>
          <F0Select
            size="md"
            hideLabel
            clearable={false}
            disabled={!firstComparison?.left.name}
            label="Operator"
            placeholder={i18n.t('workflows.sidepanel.condition.paths.operator_placeholder')}
            options={operatorOptionsFor(firstComparison?.left.name)}
            value={firstComparison?.operator}
            onChange={(op: OperatorEnum) => handleOperatorSelection(0, op)}
            error={operator ? operatorMessage : undefined}
          />
        </Box>

        {!isPeopleGroupOperator(firstComparison?.operator) && (
          <Box style={{ flex: '1', minWidth: '0' }}>
            <RightHandSelector
              variableName={firstComparison?.left.name}
              value={firstComparison?.right.value}
              operator={firstComparison?.operator}
              disabled={!firstComparison?.left.name || !firstComparison?.operator}
              onChange={firstComparison ? createLiteralChangeHandler(0, firstComparison) : () => {}}
              variablesConfig={variablesConfig}
              error={value ? rightMessage : undefined}
              variableData={getVariableData(
                firstComparison?.left.name,
                firstComparison?.left.source
              )}
            />
          </Box>
        )}
      </Box>

      {isPeopleGroupOperator(firstComparison?.operator) &&
        (() => {
          const config = variablesConfig[firstComparison?.left.name ?? '']
          if (config?.type !== 'people_group_target') return null
          const EditorComponent = config.component
          return (
            <EditorComponent
              value={firstComparison?.right.value ?? EMPTY_PEOPLE_GROUP_SPECS_VALUE}
              onChange={(newValue) => handleLiteralSelection(0, newValue, 'people_group_specs')}
              error={value ? rightMessage : undefined}
            />
          )
        })()}

      {/* Requirements list */}
      <Box
        width="fit-content"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs100' }}
        paddingX="s8"
        paddingY="s2"
        inline
      >
        <Text size="100" color="textSecondary" weight="medium">
          {i18n.t('workflows.sidepanel.condition.paths.and')}
        </Text>
      </Box>

      <Box flexDirection="row" gap="s12" alignItems="stretch" paddingLeft={'s20'} width="full">
        <Divider vertical />

        <Box flexGrow gap="s4" style={{ flex: '1', minWidth: '0' }}>
          {comparisons.map((comparison, index) => {
            if (index === 0) return null

            const indexedErrors = getFieldState(
              `comparisons.${comparisonPos}.expression.root.expressions.${index}`,
              formState
            ).error

            const {
              leftMessage: indexedLeftMessage,
              operatorMessage: indexedOperatorMessage,
              rightMessage: indexedRightMessage,
            } = getComparisonErrorMessages(indexedErrors)

            const {
              variable: indexedVariable,
              operator: indexedOperator,
              value: indexedValue,
            } = getFieldErrors(expression, index)

            return (
              <Box key={`req-${index}`} gap="s8" paddingBottom="s8" width="full">
                <Box
                  flexDirection="row"
                  gap="s8"
                  alignItems="flexStart"
                  width="full"
                  justifyContent="flexStart"
                >
                  <Box style={{ flex: '1', minWidth: '0' }}>
                    <F0Select
                      size="md"
                      hideLabel
                      clearable={false}
                      label="field"
                      placeholder={i18n.t('workflows.sidepanel.condition.paths.select_placeholder')}
                      options={selectOptions}
                      value={comparison.left.name}
                      onChange={(variableName: string) =>
                        handleVariableSelection(index, variableName)
                      }
                      error={indexedVariable ? indexedLeftMessage : undefined}
                    />
                  </Box>

                  <Box style={{ flex: '1', minWidth: '0' }}>
                    <F0Select
                      size="md"
                      hideLabel
                      clearable={false}
                      disabled={!comparison.left.name}
                      label="equals"
                      placeholder={i18n.t(
                        'workflows.sidepanel.condition.paths.operator_placeholder'
                      )}
                      options={operatorOptionsFor(comparison.left.name)}
                      value={comparison.operator}
                      onChange={(op: OperatorEnum) => handleOperatorSelection(index, op)}
                      error={indexedOperator ? indexedOperatorMessage : undefined}
                    />
                  </Box>

                  {!isPeopleGroupOperator(comparison.operator) && (
                    <Box style={{ flex: '1', minWidth: '0' }}>
                      <RightHandSelector
                        variableName={comparison.left.name}
                        value={comparison.right.value}
                        operator={comparison.operator}
                        disabled={!comparison.left.name || !comparison.operator}
                        onChange={createLiteralChangeHandler(index, comparison)}
                        variablesConfig={variablesConfig}
                        error={indexedValue ? indexedRightMessage : undefined}
                        variableData={getVariableData(comparison.left.name, comparison.left.source)}
                      />
                    </Box>
                  )}

                  <Box style={{ flexShrink: '0' }} paddingTop="s4">
                    <F0Button
                      icon={Delete}
                      label={i18n.t('common.delete')}
                      hideLabel
                      onClick={() => handleDeleteComparison(index)}
                      size="md"
                      variant="critical"
                    />
                  </Box>
                </Box>

                {isPeopleGroupOperator(comparison.operator) &&
                  (() => {
                    const config = variablesConfig[comparison.left.name ?? '']
                    if (config?.type !== 'people_group_target') return null
                    const EditorComponent = config.component
                    return (
                      <EditorComponent
                        value={comparison.right.value ?? EMPTY_PEOPLE_GROUP_SPECS_VALUE}
                        onChange={(newValue) =>
                          handleLiteralSelection(index, newValue, 'people_group_specs')
                        }
                        error={indexedValue ? indexedRightMessage : undefined}
                      />
                    )
                  })()}
              </Box>
            )
          })}

          <Box flexDirection="row" gap="s8" alignItems="flexStart" paddingBottom="s8">
            <F0Button
              label={i18n.t('workflows.sidepanel.condition.paths.add_requirement')}
              icon={Plus}
              variant="ghost"
              size="sm"
              disabled={Object.values(getFieldErrors(expression, 0)).some((error) => error)}
              onClick={handleAddRequirement}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const ConditionPathCard = ({
  comparisonPos,
  expression,
  setExpression,
  variablesConfig,
  priority,
  onRemove,
  canRemove,
  conditionsHeaderLabel,
  dynamicInputsHeaderLabel,
}: {
  comparisonPos: number
  expression: WorkflowsExpression
  setExpression: (newExpression: WorkflowsExpression) => void
  variablesConfig: VariablesConfig
  priority: number
  onRemove: () => void
  canRemove: boolean
  conditionsHeaderLabel?: string
  dynamicInputsHeaderLabel?: string
}) => {
  const [open, toggle] = useToggle(true)

  return (
    <CollapsableCard
      open={open}
      Card={
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          width="full"
          cursor="pointer"
          onClick={toggle(!open)}
        >
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Badge>{priority}</Badge>
            <Text size="200" weight="medium">
              {i18n.t('workflows.sidepanel.condition.paths.path')}
            </Text>
            <F0Icon icon={open ? ChevronDown : ChevronRight} size="md" />
          </Box>
          {canRemove && (
            <F0Button
              icon={Delete}
              label={i18n.t('common.delete')}
              hideLabel
              onClick={onRemove}
              size="md"
              variant="critical"
            />
          )}
        </Box>
      }
      Content={
        open && (
          <ConditionalPathCardContent
            comparisonPos={comparisonPos}
            expression={expression}
            setExpression={setExpression}
            variablesConfig={variablesConfig}
            conditionsHeaderLabel={conditionsHeaderLabel}
            dynamicInputsHeaderLabel={dynamicInputsHeaderLabel}
          />
        )
      }
      contentPadding="s0"
      contentBackground="grey000White"
    />
  )
}

export const ConditionPathHeader = ({ onAddCondition }: { onAddCondition: () => void }) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
      <Text size="300" weight="medium" color="textPrimary">
        {i18n.t('workflows.sidepanel.condition.paths.title')}
      </Text>
      <F0Button
        label={i18n.t('workflows.sidepanel.condition.paths.new')}
        icon={Plus}
        variant="outline"
        size="md"
        onClick={onAddCondition}
      />
    </Box>
  )
}
